export const ROUTES = {
  SIGN_IN: '/sign-in',
  ONBOARDING: '/onboarding',
} as const;

export const WORKSPACE_SECTIONS = {
  dashboard: 'dashboard',
  pullRequests: 'pull-requests',
  findings: 'findings',
  governance: 'governance',
  auditLog: 'audit-log',
  mcp: 'mcp',
  settings: 'settings',
} as const;

export type WorkspaceSectionKey = keyof typeof WORKSPACE_SECTIONS;

export function buildWorkspacePath(
  orgSlug: string,
  section: WorkspaceSectionKey,
) {
  return `/${orgSlug}/${WORKSPACE_SECTIONS[section]}`;
}

export function buildDemoWorkspacePath(section: WorkspaceSectionKey) {
  return `/demo/${WORKSPACE_SECTIONS[section]}`;
}

export function buildDashboardPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'dashboard');
}

export function buildPullRequestsPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'pullRequests');
}

export function buildFindingsPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'findings');
}

export function buildGovernancePath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'governance');
}

export type WorkspaceSearch = Record<string, string>;

export function buildProjectSearch(
  projectSlug?: string | null,
  extraSearch?: WorkspaceSearch,
): WorkspaceSearch | undefined {
  const search: WorkspaceSearch = { ...(extraSearch ?? {}) };
  if (projectSlug) search.project = projectSlug;
  return Object.keys(search).length > 0 ? search : undefined;
}

export function buildAuditLogPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'auditLog');
}

export function buildMcpPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'mcp');
}

export function buildSettingsPath(orgSlug: string) {
  return buildWorkspacePath(orgSlug, 'settings');
}

// Generic helper for org-scoped paths when the subpath is data-driven
// (e.g. post-onboarding redirect honoring a demo `next=` param). Caller is
// responsible for ensuring `subpath` starts with `/`.
export function buildOrgPath(orgSlug: string, subpath: string) {
  return `/${orgSlug}${subpath}`;
}

// Shared by the demo CTA and the demo read-only toast — both deep-link into
// `/sign-up` with the originating page so onboarding can land the visitor
// back where they started.
export function buildDemoSignUpHref(next: string) {
  const params = new URLSearchParams({ from: 'demo', next });
  return `/sign-up?${params.toString()}`;
}

export function isDemoPath(pathname: string): boolean {
  return pathname === '/demo' || pathname.startsWith('/demo/');
}

// Maps a /demo pathname to its real-workspace equivalent for onboarding's
// `next=` deep-link (e.g. /demo/findings → /findings). The bare /demo root
// maps to /dashboard since that's where demo/index.tsx redirects to.
export function stripDemoPathPrefix(pathname: string): string {
  const stripped = pathname.replace(/^\/demo(?=\/|$)/, '');
  return stripped === '' ? '/dashboard' : stripped;
}
